/**
 * 拼图边缘曲线生成器
 * 生成贝塞尔曲线控制点，创建凹凸拼接边
 */
import { Vector2, HexCoord, EdgeData, GameConfig, EdgeDirection } from '../types';
import { Random } from './Random';
import { HexMath } from './HexMath';

export class EdgeGenerator {
    private random: Random;
    private config: GameConfig;
    private worker: Worker | null = null;

    // 边缘数据缓存 (key -> { points: Vector2[], flip: boolean })
    private edgeCache: Map<string, { points: Vector2[], flip: boolean }> = new Map();
    // 路径段缓存 (edgeKey_reverse -> pathString)
    private pathSegmentCache: Map<string, string> = new Map();

    // 当前边的形状参数
    private flip: boolean = false;
    private a: number = 0;
    private b: number = 0;
    private c: number = 0;
    private d: number = 0;
    private e: number = 0;

    constructor(config: GameConfig) {
        this.config = config;
        this.random = new Random(config.seed);
        this.flip = false;
        this.a = 0;
        this.b = 0;
        this.c = 0;
        this.d = 0;
        this.e = 0;
    }

    /**
     * 更新配置
     */
    updateConfig(config: Partial<GameConfig>): void {
        this.config = { ...this.config, ...config };
    }

    /**
     * 生成所有边缘数据
     */
    generateAllEdges(): Map<string, { points: Vector2[], flip: boolean }> {
        this.edgeCache.clear();
        this.pathSegmentCache.clear();
        this.random.reset();

        const n = this.config.rings + 1;
        const yl = 2 * n - 1;

        // 生成横向曲折边 (Horizontal Zig-Zag Edges)
        for (let yi = -yl + 2; yi <= yl - 2; yi += 2) {
            const xl = 2 * n - 1 - (Math.abs(yi) - 1) / 2;
            for (let xi = -xl + 1; xi < xl - 1; xi++) {
                const yeven = ((yi + 1) % 4 === 0);
                const xeven = (xi % 2 === 0);
                const yoff = (yeven ? xeven : !xeven) ? (-1.0 / 3) : (1.0 / 3);

                const v1 = this.process(xi, yi + yoff);
                const v2 = this.process(xi + 1, yi - yoff);

                const pts = this.computeEdgePoints(v1, v2);
                this.edgeCache.set(`h_${xi}_${yi}`, { points: pts, flip: this.flip });
            }
        }

        // 生成纵向边 (Vertical Edges)
        for (let yi = -yl; yi < yl; yi += 2) {
            const xl = 2 * n - 1 - Math.abs(yi + 1) / 2;
            for (let xi = -xl + 2; xi <= xl - 2; xi += 2) {
                const v1 = this.process(xi, yi + 1.0 / 3);
                const v2 = this.process(xi, yi + 5.0 / 3);

                const pts = this.computeEdgePoints(v1, v2);
                this.edgeCache.set(`v_${xi}_${yi}`, { points: pts, flip: this.flip });
            }
        }

        return this.edgeCache;
    }

    /**
     * 获取边缘曲线点
     */
    getEdgePoints(key: string): Vector2[] | undefined {
        return this.edgeCache.get(key)?.points;
    }

    /**
     * 获取边缘翻转状态
     */
    getEdgeFlip(key: string): boolean | undefined {
        return this.edgeCache.get(key)?.flip;
    }

    /**
     * 检查边是否存在（内部边 vs 边界边）
     */
    hasEdge(key: string): boolean {
        return this.edgeCache.has(key);
    }

    /**
     * 获取碎片的逻辑中心点（像素坐标）
     */
    public getPieceCenter(coord: HexCoord): Vector2 {
        return this.process(coord.u, coord.v);
    }

    /**
     * 坐标处理流水线：缩放 -> 扭曲 -> 平移
     */
    private process(x: number, y: number): Vector2 {
        let vec = this.scale(x, y);
        vec = this.warp(vec);
        vec = this.translate(vec);
        return vec;
    }

    /**
     * 缩放到画布尺寸
     */
    private scale(x: number, y: number): Vector2 {
        const n = this.config.rings + 1;
        const radius = this.config.imageSize / 2;
        const scaleFactor = 1.0 / (2 * n - 1);

        return {
            x: x * scaleFactor * radius,
            y: y * scaleFactor * radius * HexMath.SQRT_075
        };
    }

    /**
     * 圆形扭曲变换
     */
    private warp(vec: Vector2): Vector2 {
        if (!this.config.enableWarp) return vec;

        const angle = Math.atan2(vec.y, vec.x) + Math.PI;
        const angle60 = angle % (Math.PI / 3);
        const angle30 = Math.abs((Math.PI / 6) - angle60);
        const l = HexMath.SQRT_075 / Math.cos(angle30);

        return {
            x: vec.x / l,
            y: vec.y / l
        };
    }

    /**
     * 平移到画布中心
     */
    private translate(vec: Vector2): Vector2 {
        const center = this.config.imageSize / 2;
        const offset = this.config.imageSize * 0.05;  // 5% 边距
        return {
            x: vec.x + center,
            y: vec.y + center
        };
    }

    /**
     * 生成下一条边的随机形状参数
     */
    private nextShapeParams(): void {
        const j = this.config.jitter;

        this.flip = this.random.bool();
        this.a = this.random.uniform(-j, j);
        this.b = this.random.uniform(-j, j);
        this.c = this.random.uniform(-j, j);
        this.d = this.random.uniform(-j, j);
        this.e = this.random.uniform(-j, j);
    }

    /**
     * 获取贝塞尔曲线的第 i 个控制点参数
     */
    private getPointParams(i: number): { l: number; w: number } {
        const t = this.config.tabSize;
        const w = (v: number) => v * (this.flip ? -1.0 : 1.0);

        switch (i) {
            case 0: return { l: 0.0, w: w(0.0) };
            case 1: return { l: 0.2, w: w(this.a) };
            case 2: return { l: 0.5 + this.b + this.d, w: w(-t + this.c) };
            case 3: return { l: 0.5 - t + this.b, w: w(t + this.c) };
            case 4: return { l: 0.5 - 2.0 * t + this.b - this.d, w: w(3.0 * t + this.c) };
            case 5: return { l: 0.5 + 2.0 * t + this.b - this.d, w: w(3.0 * t + this.c) };
            case 6: return { l: 0.5 + t + this.b, w: w(t + this.c) };
            case 7: return { l: 0.5 + this.b + this.d, w: w(-t + this.c) };
            case 8: return { l: 0.8, w: w(this.e) };
            case 9: return { l: 1.0, w: w(0.0) };
            default: return { l: 0, w: 0 };
        }
    }

    /**
     * 计算边缘的贝塞尔曲线控制点
     */
    private computeEdgePoints(v1: Vector2, v2: Vector2): Vector2[] {
        this.nextShapeParams();

        const dl = HexMath.sub(v2, v1);  // 边的方向向量
        const dw = HexMath.rot90(dl);    // 垂直方向（用于凹凸）

        const points: Vector2[] = [];

        for (let i = 0; i <= 9; i++) {
            const p = this.getPointParams(i);
            let vec = HexMath.add(v1, HexMath.mul(p.l, dl));
            vec = HexMath.add(vec, HexMath.mul(p.w, dw));
            points.push(vec);
        }

        return points;
    }

    /**
     * 获取横向边的端点
     */
    getHorizontalEndpoints(x: number, y: number): { v1: Vector2; v2: Vector2 } {
        const yeven = ((y + 1) % 4 === 0);
        const xeven = (x % 2 === 0);
        const yoff = (yeven ? xeven : !xeven) ? (-1.0 / 3) : (1.0 / 3);

        return {
            v1: this.process(x, y + yoff),
            v2: this.process(x + 1, y - yoff)
        };
    }

    /**
     * 获取纵向边的端点
     */
    getVerticalEndpoints(x: number, y: number): { v1: Vector2; v2: Vector2 } {
        return {
            v1: this.process(x, y + 1.0 / 3),
            v2: this.process(x, y + 5.0 / 3)
        };
    }

    /**
     * 生成贝塞尔曲线的 SVG 路径字符串
     */
    getBezierPath(points: Vector2[], reverse: boolean = false): string {
        const pts = reverse ? [...points].reverse() : points;
        let d = '';

        // 3段三次贝塞尔曲线
        for (let i = 1; i < 10; i += 3) {
            d += `C ${pts[i].x.toFixed(2)} ${pts[i].y.toFixed(2)} ` +
                `${pts[i + 1].x.toFixed(2)} ${pts[i + 1].y.toFixed(2)} ` +
                `${pts[i + 2].x.toFixed(2)} ${pts[i + 2].y.toFixed(2)} `;
        }

        return d;
    }

    /**
     * 生成完整的碎片路径
     */
    createPiecePath(coord: HexCoord): string {
        const edges = HexMath.getPieceEdges(coord);

        // 计算起点
        const startEnds = this.getVerticalEndpoints(coord.u - 1, coord.v - 1);
        const startPt = startEnds.v1;

        let d = `M ${startPt.x.toFixed(2)} ${startPt.y.toFixed(2)} `;

        // 遍历6条边
        for (const edge of edges) {
            const pts = this.getEdgePoints(edge.key);

            if (pts) {
                // 内部边：画贝塞尔曲线（使用缓存）
                const cacheKey = `${edge.key}_${edge.reverse}`;
                let segment = this.pathSegmentCache.get(cacheKey);
                if (!segment) {
                    segment = this.getBezierPath(pts, edge.reverse);
                    this.pathSegmentCache.set(cacheKey, segment);
                }
                d += segment;
            } else {
                // 边界边：画直线
                const ends = edge.type === 'h'
                    ? this.getHorizontalEndpoints(edge.x, edge.y)
                    : this.getVerticalEndpoints(edge.x, edge.y);

                const target = edge.reverse ? ends.v1 : ends.v2;
                d += `L ${target.x.toFixed(2)} ${target.y.toFixed(2)} `;
            }
        }

        d += 'Z';
        return d;
    }

    /**
     * 检查碎片是否有有效的曲线边（至少一条）
     */
    hasValidEdges(coord: HexCoord): boolean {
        const edges = HexMath.getPieceEdges(coord);
        return edges.some(edge => this.edgeCache.has(edge.key));
    }

    /**
     * 检查碎片是否为边缘碎片
     * 边缘碎片定义：至少有一条边是直线（不在 edgeCache 中）
     */
    isEdgePiece(coord: HexCoord): boolean {
        const edges = HexMath.getPieceEdges(coord);
        return edges.some(edge => !this.edgeCache.has(edge.key));
    }

    /**
     * 获取所有有效碎片的坐标
     */
    getValidPieceCoords(): HexCoord[] {
        const n = this.config.rings;
        const range = 2 * n + 2;
        const coords: HexCoord[] = [];

        for (let v = -range; v <= range; v += 2) {
            for (let u = -range; u <= range; u++) {
                const coord = { u, v };

                if (!HexMath.isValidHexCoord(coord)) continue;
                if (!this.hasValidEdges(coord)) continue;

                coords.push(coord);
            }
        }

        return coords;
    }

    destroy(): void {
        if (this.worker) {
            this.worker.terminate();
            this.worker = null;
        }
    }
}
